/**
 * A week's list is one row per WORKS ORDER, not one per operation. Split from
 * viewmodels/loading on the CD-1 module budget.
 */

import type { LoadDrillRow } from '../types/loading'

/** One WORKS ORDER in a week's list, not one operation (20 Aug 2026, Mike).
 *
 *  A job can hit the same machine group twice in a week — 9620.22 runs op 10 and op 20 on
 *  MATSURA in w/c 12 Oct, 6h and 37h — and the list showed that as two rows five apart,
 *  splitting 43h of one promise into two numbers a planner has to add up. The route that
 *  opens under the row already lists every operation, with its number and its dates, so
 *  the second row was saying nothing the expansion did not say better. */
export interface WoLine {
  woKey: string
  part: string | null
  ops: number[]
  hours: number
  requiredDate: string | null
  isOverdue: boolean
}

/** Group a section's operations by works order, keeping the order they arrived in — the
 *  backend sorts oldest arrears first and that is the reading the section label promises.
 *
 *  Hours SUM, because the section's total has to keep meaning the same thing. The required
 *  date is the EARLIEST of the operations' — the most binding version of the promise — and
 *  the overdue flag is the worst of them, for the same reason. */
export function byWorksOrder(rows: LoadDrillRow[]): WoLine[] {
  const lines = new Map<string, WoLine>()
  for (const row of rows) {
    const hours = row.levelled_hours ?? row.load_hours
    const line = lines.get(row.wo_key)
    if (!line) {
      lines.set(row.wo_key, {
        woKey: row.wo_key,
        part: row.part_number,
        ops: [row.op_number],
        hours,
        requiredDate: row.required_date,
        isOverdue: row.is_overdue,
      })
      continue
    }
    line.ops.push(row.op_number)
    line.hours += hours
    line.isOverdue = line.isOverdue || row.is_overdue
    if (row.required_date && (!line.requiredDate || row.required_date < line.requiredDate)) {
      line.requiredDate = row.required_date
    }
  }
  // Ascending: an operation list reads as a route, and a route runs 10, 20, 30 — the order
  // the rows arrived in is the queue's order, which is not the same thing.
  for (const line of lines.values()) line.ops.sort((a, b) => a - b)
  return [...lines.values()]
}
